import classNames from 'classnames';
import { useFormik } from 'formik';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLogin, useStaffLogin } from '../../../apis/hooks/auth';
import Button from '../../../components/bootstrap/Button';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import Spinner from '../../../components/bootstrap/Spinner';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import AuthContext from '../../../contexts/authContext';
import useDarkMode from '../../../hooks/useDarkMode';
import Page from '../../../layout/Page/Page';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Logo from '../../../assets/logos/neels_logo.png';
import { MotivationalQuotes } from '../../../MotivationalQuotes';

interface ILoginHeaderProps {
	isNewUser?: boolean;
}
const LoginHeader: FC<ILoginHeaderProps> = ({ isNewUser }) => {
	// if (isNewUser) {
	// 	return (
	// 		<>
	// 			<div className='text-center h2 fw-bold mt-5'>Create Account,</div>
	// 			<div className='text-center h5 text-muted mb-5'>Sign up to get started!</div>
	// 		</>
	// 	);
	// }
	return (
		<>
			<div className='text-center h2 fw-bold mt-5'>Welcome,</div>
			<div className='text-center h5 text-muted mb-5'>Sign in to continue!</div>
		</>
	);
};

interface ILoginProps {
	isSignUp?: boolean;
}

function decodeJWT(token: string) {
	try {
		const payload = token.split('.')[1];
		const decoded = JSON.parse(atob(payload));
		return decoded;
	} catch (error) {
		console.error('Invalid token', error);
		return null;
	}
}

function isTokenExpired(token: string): boolean {
	const decoded = decodeJWT(token);
	if (!decoded?.exp) return true;

	const now = Math.floor(Date.now() / 1000); // Current time in seconds
	return decoded.exp < now;
}

function getTokenExpiryDate(token: string): string | null {
	const decoded = decodeJWT(token);
	if (!decoded?.exp) return null;

	const expiry = new Date(decoded.exp * 1000); // Convert to ms
	return expiry.toString();
}

const Login: FC<ILoginProps> = ({ isSignUp }) => {
	const { setUser, setToken, setRights } = useContext(AuthContext);

	const { darkModeStatus } = useDarkMode();

	const [signInPassword, setSignInPassword] = useState<boolean>(false);
	const [singUpStatus, setSingUpStatus] = useState<boolean>(!!isSignUp);

	const [showPassword, setShowPassword] = useState(false);

	const navigate = useNavigate();
	const handleOnClick = useCallback(() => navigate('/'), [navigate]);

	const { mutate: mutateStaffLogin, isError, error } = useStaffLogin();
	const { mutate: mutateLogin } = useLogin();

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginUsername: '', // admin@admin.com
			// loginPassword: 'Neels@2025$05', // Old Password
			loginPassword: '', // New Password Neels@2025$07
		},
		validate: (values) => {
			const errors: { loginUsername?: string; loginPassword?: string } = {};

			return errors;
		},
		validateOnChange: false,
		onSubmit: (values) => {
			if (!values.loginUsername) {
				toast.error('Usernam\ is required', {
					closeOnClick: true,
					closeButton: true,
				});
				return;
			}

			if (!values.loginPassword) {
				toast.error('Password is required', {
					closeOnClick: true,
					closeButton: true,
				});
				return;
			}
			const loginPayload = {
				username: values.loginUsername,
				password: values.loginPassword,
			};

			const onAdminLoginSuccess = (res: any) => {
				console.log("Login Response ==> ", res)
				if (res?.status === true) {
					if (setUser && setToken && setRights) {
						setUser(JSON.stringify(res?.data?.user));
						setToken(res?.data?.tokens?.accessToken);
						setRights(JSON.stringify(res?.data?.rights));
					}
					toast.success('Login successfully', {
						closeOnClick: true,
						closeButton: true,
					});
					localStorage.setItem('accessToken', res?.data?.tokens?.accessToken);
					localStorage.setItem('refreshToken', res?.data?.tokens?.refreshToken);
					localStorage.setItem('username', res?.data?.user?.username);
					localStorage.setItem('receverId', res?.data?.user?._id);
					localStorage.setItem('loggedin_user_id', res?.data?.user?._id);
					localStorage.setItem('neels_rights', JSON.stringify(res?.data?.rights));
					localStorage.setItem('neels_authUsername', JSON.stringify(res?.data?.user));
					handleOnClick();
				} else {
					toast.error(res?.message, {
						closeOnClick: true,
						closeButton: true,
					});
				}
			};

			const onStaffLoginSuccess = (res: any) => {
				console.log("Login Response ==> ", res)
				if (res?.status === true) {
					if (setUser && setToken && setRights) {
						setUser(JSON.stringify(res?.data?.user));
						setToken(res?.data?.tokens?.accessToken);
						setRights(JSON.stringify(res?.data?.rights));
					}
					toast.success('Login successfully', {
						closeOnClick: true,
						closeButton: true,
					});
					localStorage.setItem('accessToken', res?.data?.tokens?.accessToken);
					localStorage.setItem('refreshToken', res?.data?.tokens?.refreshToken);
					localStorage.setItem('username', res?.data?.user?.username);
					localStorage.setItem('receverId', res?.data?.user?._id);
					localStorage.setItem('loggedin_user_id', res?.data?.staff?._id);
					localStorage.setItem('neels_rights', JSON.stringify(res?.data?.rights));
					localStorage.setItem('neels_authUsername', JSON.stringify(res?.data?.user));
					handleOnClick();
				} else {
					toast.error(res?.message, {
						closeOnClick: true,
						closeButton: true,
					});
				}
			};

			const onError = () => {
				toast.error('Username and password do not match.', {
					closeOnClick: true,
					closeButton: true,
				});
			};

			// Conditionally use admin or staff login
			if (values.loginUsername === 'admin@admin.com') {
				mutateLogin(loginPayload, { onSuccess: onAdminLoginSuccess, onError });
			} else {
				mutateStaffLogin(loginPayload, { onSuccess: onStaffLoginSuccess, onError });
			}
		},
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const handleContinue = () => {
		setIsLoading(true);
		setTimeout(() => {
			setSignInPassword(true);
			setIsLoading(false);
		}, 300);
	};

	const [quote, setQuote] = useState<string>('');

	useEffect(() => {
		const randomIndex = Math.floor(Math.random() * MotivationalQuotes?.length);
		setQuote(MotivationalQuotes[randomIndex]);
	}, []);

	return (
		<PageWrapper isProtected={false} title={'Login'} className={'bg-light'}>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
						<Card className='shadow-3d-dark' data-tour='login-page'>
							<CardBody>
								<div className='text-center my-5'>
									<Link
										to='/'
										className={classNames(
											'text-decoration-none  fw-bold display-6',
										)}
										aria-label='Facit'>
										{/* <Logo width={200} /> */}
										<img
											src={Logo}
											alt=''
											width={200}
											style={{ filter: 'invert(1)' }}
										/>
										{/* <h1>Neel's Sanjayraj</h1> */}
									</Link>
								</div>
								{/* <div
									className={classNames('rounded-3', {
										'bg-l10-dark': !darkModeStatus,
										'bg-dark': darkModeStatus,
									})}>
									<div className='row row-cols-1 g-3 pb-3 px-3 mt-0'>
										<div className='col'> */}
											{/* <Button
												color={darkModeStatus ? 'light' : 'dark'}
												isLight={singUpStatus}
												className='rounded-1 w-100'
												size='lg'
												onClick={() => {
													setSignInPassword(false);
													setSingUpStatus(!singUpStatus);
												}}>
												Login
											</Button> */}
											{/* <div
												style={{
													backgroundColor: '#2b2b2b',
													color: '#fff',
													padding: '14px',
													borderRadius: '12px',
													textAlign: 'center',
													fontSize: '14px',
													fontWeight: 500,
												}}
											>
												To request deletion of your data, please enter your registered email below.
											</div> */}
										{/* </div>
									</div> */}
								{/* </div> */}

								<LoginHeader isNewUser={singUpStatus} />

								<form
									className='row g-4'
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											e.preventDefault();

											if (!signInPassword) {
												if (!formik.values.loginUsername) return;
												handleContinue();
											} else {
												formik.handleSubmit();
											}
										}
									}}>
									{/* {singUpStatus ? (
										<>
											<div className='col-12'>
												<FormGroup
													id='signup-email'
													isFloating
													label='Your email'>
													<Input type='email' autoComplete='email' />
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='signup-name'
													isFloating
													label='Your name'>
													<Input autoComplete='given-name' />
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='signup-surname'
													isFloating
													label='Your surname'>
													<Input autoComplete='family-name' />
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='signup-password'
													isFloating
													label='Password'>
													<Input
														type='password'
														autoComplete='password'
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<Button
													color='info'
													className='w-100 py-3'
													onClick={handleOnClick}>
													Sign Up
												</Button>
											</div>
										</>
									) : ( */}
									<>
										<div className='col-12'>
											<FormGroup
												id='loginUsername'
												isFloating
												label='Your email or username'
												className={classNames({
													'd-none': signInPassword,
												})}>
												<Input
													autoComplete='username'
													value={formik.values.loginUsername}
													isTouched={formik.touched.loginUsername}
													invalidFeedback={formik.errors.loginUsername}
													isValid={formik.isValid}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													onFocus={() => {
														formik.setErrors({});
													}}
												/>
											</FormGroup>
											{signInPassword && (
												<div className='text-center h4 mb-3 fw-bold'>
													Hi, {formik.values.loginUsername}
												</div>
											)}
											<FormGroup
												id='loginPassword'
												isFloating
												label='Password'
												className={classNames(
													{
														'd-none': !signInPassword,
													},
													'position-relative',
												)}>
												<Input
													id='loginPassword' // important for label + accessibility
													name='loginPassword' // 👈 required for Formik to track this field
													type={showPassword ? 'text' : 'password'}
													autoComplete='current-password'
													value={formik.values.loginPassword}
													isTouched={formik.touched.loginPassword}
													invalidFeedback={formik.errors.loginPassword}
													validFeedback='Looks good!'
													isValid={formik.isValid}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													className='pe-5'
												/>
												<Button
													// If Button complains about `type`, remove it — it's optional
													type='button'
													size='sm'
													// keep it visually minimal — override with CSS below
													className='password-toggle-btn'
													onClick={() => setShowPassword((s) => !s)}
													aria-label={
														showPassword
															? 'Hide password'
															: 'Show password'
													}
													title={
														showPassword
															? 'Hide password'
															: 'Show password'
													}
													icon={
														showPassword
															? 'VisibilityOff'
															: 'Visibility'
													} // use Facit icon name used across your project
												/>
											</FormGroup>
										</div>
										<div className='col-12'>
											{!signInPassword ? (
												<Button
													color='info'
													className='w-100 py-3'
													isDisable={!formik.values.loginUsername}
													onClick={handleContinue}>
													{isLoading && (
														<Spinner isSmall inButton isGrow />
													)}
													Continue
												</Button>
											) : (
												<Button
													color='info'
													className='w-100 py-3'
													onClick={formik.handleSubmit}>
													Login
												</Button>
											)}
										</div>
									</>
									{/* )} */}

									{/* BEGIN :: Social Login */}
									{/* {!signInPassword && (
										<>
											<div className='col-12 mt-3 text-center text-muted'>
												OR
											</div>
											<div className='col-12 mt-3'>
												<Button
													isOutline
													color={darkModeStatus ? 'light' : 'dark'}
													className={classNames('w-100 py-3', {
														'border-light': !darkModeStatus,
														'border-dark': darkModeStatus,
													})}
													icon='CustomApple'
													onClick={handleOnClick}>
													Sign in with Apple
												</Button>
											</div>
											<div className='col-12'>
												<Button
													isOutline
													color={darkModeStatus ? 'light' : 'dark'}
													className={classNames('w-100 py-3', {
														'border-light': !darkModeStatus,
														'border-dark': darkModeStatus,
													})}
													icon='CustomGoogle'
													onClick={handleOnClick}>
													Continue with Google
												</Button>
											</div>
										</>
									)} */}
									{/* END :: Social Login */}
								</form>
							</CardBody>
						</Card>
						{/* <div className='text-center'>
							<a
								href='/'
								className={classNames('text-decoration-none me-3', {
									'link-light': singUpStatus,
									'link-dark': !singUpStatus,
								})}>
								Privacy policy
							</a>
							<a
								href='/'
								className={classNames('link-light text-decoration-none', {
									'link-light': singUpStatus,
									'link-dark': !singUpStatus,
								})}>
								Terms of use
							</a>
						</div> */}
						<div className='quote-glass'>
							<span className='quote-text'>“{quote}”</span>
							<div className='quote-underline' />
						</div>

						<div className='flex justify-between mt-4' style={{ display: 'flex', justifyContent: 'space-between' }}>
							<div>
								<a href="/terms-and-condition">Terms & Conditions</a>
							</div>
							<div>
								<a href="/privacy-policy">Privacy Policy</a>
							</div>
							<div>
								<a href="/deleteData">Delete Your Data</a>
							</div>
						</div>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default Login;
